import { useMemo } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { useProfileStore } from '@/store/useProfileStore';
import { ROLE_LABEL, can, capabilitiesFor, missingReason, type Capability } from './capabilities';

/**
 * The signed-in person, session status and what they may do. Components gate
 * on `can('documents.write')`, never on role strings. A control the user can
 * see but not use renders DISABLED with `locked(...)` as its tooltip.
 */
export function useSession() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const status = useAuthStore((s) => s.sessionStatus);
  const me = useProfileStore((s) => s.me);
  const capabilities = useMemo(() => capabilitiesFor(me?.profile.roles), [me?.profile.roles]);

  return {
    isAuthenticated,
    status,
    me,
    capabilities,
    can: (c: Capability) => can(capabilities, c),
    locked: (c: Capability) => (can(capabilities, c) ? undefined : missingReason(c)),
  };
}

export function displayName(me: { profile: { displayName?: string; firstName?: string; email?: string } } | null) {
  if (!me) return '';
  return me.profile.displayName || me.profile.firstName || me.profile.email || '';
}

export function initialsOf(me: { profile: { initials?: string; firstName?: string; lastName?: string } } | null) {
  if (!me) return '';
  if (me.profile.initials) return me.profile.initials;
  return `${me.profile.firstName?.[0] ?? ''}${me.profile.lastName?.[0] ?? ''}`.toUpperCase();
}

export function roleLabel(roles: readonly string[] | undefined): string {
  const names = (roles ?? []).map((r) => ROLE_LABEL[r]).filter(Boolean);
  return names.length ? names.join(' · ') : 'No access';
}
